import React, { useState, useEffect } from 'react';
import { Download, Chrome, Info, CheckCircle2, AlertCircle } from 'lucide-react';

const AddonPage = () => {
  const [downloadLink, setDownloadLink] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLatestVersion = async () => {
      try {
        const isDev = window.location.port === "3000";
        const fetchUrl = isDev 
          ? 'https://myanimetracker.online/addon/updates.json' 
          : '/addon/updates.json';
          
        const res = await fetch(fetchUrl);
        if (res.ok) {
          const contentType = res.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const data = await res.json();
            const addonId = Object.keys(data.addons)[0];
            const latestUpdate = data.addons[addonId].updates[0];
            setDownloadLink(latestUpdate.update_link);
          } else if (isDev) {
            // En dev, si nginx bloque CORS ou si on a du HTML, on fallback sur une URL en dur
            setDownloadLink('https://myanimetracker.online/addon/myanimetracker_v1.1.xpi');
          } else {
             setError("L'extension n'est pas encore disponible sur ce serveur (Format invalide).");
          }
        } else {
          setError("L'extension n'est pas encore disponible sur ce serveur.");
        }
      } catch (err) {
        // En cas d'erreur CORS en local, on fallback
        if (window.location.port === "3000") {
            setDownloadLink('https://myanimetracker.online/addon/myanimetracker_v1.1.xpi');
        } else {
            setError("Impossible de vérifier la dernière version de l'extension.");
        }
      }
    };
    fetchLatestVersion();
  }, []);

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gray-900 p-6 flex flex-col items-center">
      <div className="max-w-3xl w-full mt-8">
        
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400 mb-4">
            MyAnimeTracker Addon
          </h1>
          <p className="text-xl text-gray-400">
            L'extension universelle pour synchroniser automatiquement vos visionnages
          </p>
        </div>

        <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden shadow-2xl mb-8">
          <div className="p-8">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <Download className="text-blue-400" />
              Téléchargement
            </h2>
            
            {error ? (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex items-start gap-3 text-red-400 mb-6">
                <AlertCircle className="shrink-0 mt-0.5" />
                <p>{error}</p>
              </div>
            ) : (
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-6 flex flex-col items-center gap-4 text-center mb-6">
                <p className="text-gray-300">
                  Choisissez votre navigateur pour télécharger l'extension MyAnimeTracker.
                </p>
                {downloadLink ? (
                  <div className="flex flex-col sm:flex-row gap-4 mt-2">
                    <a 
                      href={downloadLink}
                      target="_blank"
                      rel="noreferrer"
                      download
                      className="bg-orange-600 hover:bg-orange-500 text-white px-6 py-3 rounded-xl font-bold transition-all shadow-lg hover:shadow-orange-500/25 flex items-center justify-center gap-3 w-full sm:w-auto"
                    >
                      <Download size={20} />
                      Firefox (.xpi)
                    </a>
                    <a 
                      href={downloadLink.replace('.xpi', '.zip')}
                      target="_blank"
                      rel="noreferrer"
                      download
                      className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 rounded-xl font-bold transition-all shadow-lg hover:shadow-blue-500/25 flex items-center justify-center gap-3 w-full sm:w-auto"
                    >
                      <Download size={20} />
                      Chrome / Edge (.zip)
                    </a>
                  </div>
                ) : (
                  <div className="animate-pulse flex flex-col sm:flex-row gap-4 mt-2">
                    <div className="bg-gray-700 h-12 w-48 rounded-xl"></div>
                    <div className="bg-gray-700 h-12 w-48 rounded-xl"></div>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-4 text-gray-300">
              <h3 className="text-lg font-semibold text-white mt-8 mb-4 border-b border-gray-700 pb-2 flex items-center gap-2">
                <Info size={20} className="text-gray-400" />
                Comment installer l'extension ?
              </h3>
              
              <div className="grid gap-6 md:grid-cols-2">
                <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700/50">
                  <h4 className="font-bold text-orange-400 mb-3 flex items-center gap-2">
                    🦊 Firefox
                  </h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-gray-400">
                    <li>Téléchargez le fichier <code className="bg-gray-900 px-1 py-0.5 rounded text-orange-300">.xpi</code></li>
                    <li>Ouvrez Firefox</li>
                    <li>Glissez-déposez le fichier <code className="bg-gray-900 px-1 py-0.5 rounded text-orange-300">.xpi</code> dans n'importe quelle fenêtre Firefox</li>
                    <li>Cliquez sur "Ajouter" dans la popup qui s'affiche</li>
                  </ol>
                </div>
                
                <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700/50">
                  <h4 className="font-bold text-green-400 mb-3 flex items-center gap-2">
                    <Chrome size={18} /> Chrome / Edge / Brave
                  </h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-gray-400">
                    <li>Téléchargez et renommez le fichier en <code className="bg-gray-900 px-1 py-0.5 rounded text-green-300">.zip</code></li>
                    <li>Extrayez le dossier</li>
                    <li>Ouvrez <code className="bg-gray-900 px-1 py-0.5 rounded text-gray-300">chrome://extensions/</code></li>
                    <li>Activez le <strong>Mode développeur</strong> en haut à droite</li>
                    <li>Cliquez sur <strong>Charger l'extension non empaquetée</strong> et sélectionnez le dossier extrait</li>
                  </ol>
                </div>
              </div>
            </div>

          </div>
          
          <div className="bg-gray-900/50 p-6 border-t border-gray-700">
            <h3 className="font-semibold text-white mb-4">Fonctionnalités incluses :</h3>
            <ul className="grid sm:grid-cols-2 gap-3 text-sm text-gray-400">
              <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-emerald-400 shrink-0" /> Suivi automatique des épisodes vus</li>
              <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-emerald-400 shrink-0" /> Synchronisation avec la base de données</li>
              <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-emerald-400 shrink-0" /> Badge de suivi sur la page des animes</li>
              <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-emerald-400 shrink-0" /> Qualité max par défaut</li>
            </ul>
          </div>
        </div>
        
      </div>
    </div>
  );
};

export default AddonPage;
